import { Typography } from "@leap4/frontend";
import React, { useEffect, useRef, useState } from "react";

/** Keeps someone company until all dashboard panels finish loading. */

/** Below this, the fetch was quick enough that saying anything would just be a flicker. */
const SHOW_AFTER_MS = 500;

/** Deliberately in fixed order rather than shuffled: the message you are on is a rough read on how */

const STAGES: { after: number; text: string }[] = [
    { after: SHOW_AFTER_MS, text: "Working on it..." },
    { after: 5_000, text: "Yes, I really am working on it." },
    { after: 10_000, text: "Counting impressions. All of them." },
    { after: 15_000, text: "Attributing... Predicting... Calculating..." },
    { after: 20_000, text: "Have you considered Day grain? Day grain is lovely." },
    { after: 25_000, text: "Day grain told me to tell you, it thinks you are lovely too." },
    { after: 30_000, text: "You deserve nice things!" },
    { after: 35_000, text: "Look at you - Being all busy and stuff." },
    { after: 40_000, text: "Can't blame you. I like it here. There's a spinner, numbers are colorful." },
    { after: 45_000, text: "Yeah, okay, at this point I would say this is taking 'long'." },
    { after: 50_000, text: "Still quicker than exporting this to Excel." },
    { after: 55_000, text: "Okay this REALLY IS taking long." },
    { after: 60_000, text: "If you selected a big time range, that's on you buddy!" },
    { after: 65_000, text: "It's been more than a full minute. Let's hope the data's worth it." },
    { after: 70_000, text: "I will say, you are great company while I work and you wait." },
    { after: 75_000, text: "At this point the question becomes, do you really want the data?" },
    { after: 80_000, text: "Or does the data want you...?"},
    { after: 85_000, text: "Okay yeah data said it wants you. Glad we cleared that up."},
    { after: 90_000, text: "This is the point where you contact the devs."},
    { after: 95_000, text: "I'm only half joking, it's been a minute and a half."},
    { after: 100_000, text: "This is normal, it happens to everyone, I swear."},
    { after: 105_000, text: "Usually if you're fast that's a bad thing, right?"},
    { after: 110_000, text: "Jokes aside, this has been taking longer than I'd normally expect."},
    { after: 115_000, text: "Long time ranges, finer grains, all of that will impact the time this takes."},
    { after: 120_000, text: "Two minutes of your life you are not getting back."},
    { after: 125_000, text: "Okay at this point you're allowed to complain to the devs."},
    { after: 130_000, text: "In fact, I insist you do."},
    { after: 135_000, text: "But while you do that..."},
    { after: 140_000, text: "Did you know that Red Crabs live in forests?"},
    { after: 145_000, text: "Did you know it takes light about 8:20 minutes to reach us from the sun?"},
    { after: 150_000, text: "Did you know that you cannot breathe through your nose while smiling?"},
    { after: 155_000, text: "Hehehe, made you smile didn't I?"},
    { after: 160_000, text: "Anyway, back to work."},
    { after: 165_000, text: "Lambdaing the pixel..."},
    { after: 170_000, text: "Pixeling the advertisers..."},
    { after: 175_000, text: "Advertising the publishers..."},
    { after: 180_000, text: "In all honesty, it's been 3 minutes exactly. I encourage you to pester a dev. This message will self destruct in 5 seconds."},
    { after: 185_000, text: "Bam! Message self destructed."}
];


const DONE_VISIBLE_MS = 2_200;

const stageFor = (elapsed: number): string =>
    [...STAGES].reverse().find((s) => elapsed >= s.after)?.text ?? "";

const GrainStatus = ({ loading }: { loading: boolean }): JSX.Element | null => {
    const [elapsed, setElapsed] = useState<number | null>(null);
    const [done, setDone] = useState(false);
    const startedAt = useRef<number | null>(null);

    useEffect(() => {
        if (loading) {
            setDone(false);
            startedAt.current = Date.now();
            setElapsed(0);
            const id = setInterval(() => {
                setElapsed(Date.now() - (startedAt.current ?? Date.now()));
            }, 250);
            return () => clearInterval(id);
        }

        // Only celebrate a wait long enough to notice, or "Done!" appears from nowhere.
        const started = startedAt.current;
        startedAt.current = null;
        setElapsed(null);
        if (started !== null && Date.now() - started >= SHOW_AFTER_MS) {
            setDone(true);
            const t = setTimeout(() => setDone(false), DONE_VISIBLE_MS);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [loading]);

    if (done) {
        return (
            <Typography variant="caption" sx={{ color: "success.main", fontWeight: 600 }}>
                Done!
            </Typography>
        );
    }

    if (elapsed === null || elapsed < SHOW_AFTER_MS) return null;

    return (
        <Typography variant="caption" sx={{ color: "warning.main", fontWeight: 600 }}>
            {stageFor(elapsed)}
        </Typography>
    );
};

export default GrainStatus;
